import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Calendar, Dumbbell, Sparkles } from "lucide-react";
import { ImportProgramModal } from "@/components/ImportProgramModal";

export default function CoachPrograms() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", program_type: "1on1", duration_weeks: 4, cover_url: "",
  });

  const load = useCallback(() => api.get("/programs").then((r) => setItems(r.data)), []);
  useEffect(() => { load().catch(() => {}); api.get("/coach/clients").then((r) => setClients(r.data.assigned || [])).catch(() => {}); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/programs", form);
      toast.success("Program created");
      setForm({ title: "", description: "", program_type: "1on1", duration_weeks: 4, cover_url: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    await api.delete(`/programs/${id}`);
    toast.success("Removed");
    load();
  };

  const toggleOnDemand = async (p) => {
    try {
      await api.patch(`/programs/${p.program_id}`, { on_demand: !p.on_demand });
      toast.success(!p.on_demand ? "Now visible in the on-demand library" : "Removed from on-demand library");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const assignToClient = async (program_id, client_id) => {
    if (!client_id) return;
    try {
      await api.post("/programs/assign", { program_id, client_id, start_date: new Date().toISOString().slice(0,10) });
      toast.success("Assigned to client");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-10" data-testid="coach-programs">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Build</div>
          <h1 className="font-serif text-5xl mt-3">Programs</h1>
          <p className="text-secondary-luxe text-sm mt-3">1:1, group, and evergreen tracks. Drop AI-generated workouts directly in here.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button className="btn-secondary" onClick={() => setImportOpen(true)} data-testid="import-program-btn">
            <Sparkles size={14} /> Import with AI
          </button>
          <button className="btn-primary" onClick={() => setOpen(!open)} data-testid="new-program-btn">
            <Plus size={14} /> New program
          </button>
        </div>
      </header>

      {importOpen && <ImportProgramModal onClose={() => setImportOpen(false)} onImported={load} />}

      {open && (
        <form onSubmit={submit} className="card-luxe space-y-5 max-w-3xl" data-testid="new-program-form">
          <div>
            <label className="label-luxe">Title</label>
            <input className="input-luxe" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="program-title" />
          </div>
          <div>
            <label className="label-luxe">Description</label>
            <textarea className="input-luxe" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="program-description" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="label-luxe">Type</label>
              <select className="input-luxe" value={form.program_type} onChange={(e) => setForm({ ...form, program_type: e.target.value })} data-testid="program-type">
                <option value="1on1">1:1</option>
                <option value="group">Group</option>
                <option value="evergreen">Evergreen</option>
              </select>
            </div>
            <div>
              <label className="label-luxe">Duration (weeks)</label>
              <input type="number" min="1" max="52" className="input-luxe" value={form.duration_weeks} onChange={(e) => setForm({ ...form, duration_weeks: parseInt(e.target.value || "4", 10) })} data-testid="program-duration" />
            </div>
            <div>
              <label className="label-luxe">Cover URL</label>
              <input className="input-luxe" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://…" />
            </div>
          </div>
          <button className="btn-primary" data-testid="create-program-submit">Create</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((p) => (
          <div key={p.program_id} className="card-luxe relative" data-testid={`program-${p.program_id}`}>
            {p.cover_url && <img src={p.cover_url} alt="" className="w-full h-32 object-cover -mx-6 -mt-6 mb-4" style={{ width: "calc(100% + 3rem)" }} />}
            <div className="flex items-center justify-between">
              <div className="overline">{p.program_type}</div>
              {p.on_demand && (
                <span className="font-label text-[9px] tracking-[0.2em] uppercase px-2 py-1"
                  style={{ background: "var(--surface-2)", color: "var(--accent)" }}
                  data-testid={`ondemand-badge-${p.program_id}`}>On-Demand</span>
              )}
            </div>
            <h3 className="font-serif text-2xl mt-2">{p.title}</h3>
            <p className="text-secondary-luxe text-sm mt-2 line-clamp-3">{p.description}</p>
            <div className="text-xs text-tertiary-luxe mt-4 flex items-center gap-2">
              <Calendar size={12} /> {p.duration_weeks} weeks · {(p.workouts || []).length} workouts
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <Link to={`/coach/programs/${p.program_id}`} className="btn-secondary text-xs inline-flex" data-testid={`build-${p.program_id}`}>
                <Dumbbell size={13} /> Build workouts
              </Link>
              <button className="btn-secondary text-xs" onClick={() => toggleOnDemand(p)} data-testid={`toggle-ondemand-${p.program_id}`}>
                {p.on_demand ? "Remove from on-demand" : "Make on-demand"}
              </button>
            </div>
            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <select className="input-luxe text-xs flex-1" onChange={(e) => assignToClient(p.program_id, e.target.value)} defaultValue="" data-testid={`assign-${p.program_id}`}>
                <option value="">Assign to client…</option>
                {clients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
              </select>
              <button className="btn-ghost" onClick={() => remove(p.program_id)} data-testid={`delete-${p.program_id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-secondary-luxe">No programs yet.</div>}
      </div>
    </div>
  );
}
